import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import config from "./config";
import './styles/ProfileSetup.css';

function ProfileSetup({ setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [bio, setBio] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Get user data from location state or localStorage
    const userFromState = location.state?.user;
    const userFromStorage = localStorage.getItem("chatapp_user");

    if (userFromState) {
      setUserData(userFromState);
    } else if (userFromStorage) {
      const parsedUser = JSON.parse(userFromStorage);
      // Only allow if profile setup is not complete
      if (!parsedUser.profileSetupComplete) {
        setUserData(parsedUser);
      } else {
        // Profile already setup, redirect to home
        navigate('/', { replace: true });
      }
    } else {
      // No user data, redirect to login
      navigate('/login', { replace: true });
    }
  }, [location, navigate]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username.trim()) {
      setErrorMessage("Username is required");
      return;
    }

    if (!userData || !userData.id) {
      setErrorMessage("User data not found. Please try logging in again.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      // Create FormData to handle both text and file uploads
      const formData = new FormData();
      formData.append('userId', userData.id);
      formData.append('username', username.trim());
      formData.append('bio', bio.trim());
      
      if (profileImage) {
        formData.append('profileImage', profileImage);
      }

      // Complete profile setup (update the endpoint to accept FormData)
      const response = await axios.post(
        `${config.apiUrl}/api/auth/complete-google-profile`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const updatedUser = response.data.user;

      // Fetch privacy settings
      try {
        const privacyRes = await axios.get(
          `${config.apiUrl}/api/auth/privacy-settings/${updatedUser.id}`
        );
        const userWithPrivacy = {
          ...updatedUser,
          privacySettings: privacyRes.data.privacySettings
        };
        setUser(userWithPrivacy);
        localStorage.setItem("chatapp_user", JSON.stringify(userWithPrivacy));
      } catch (privacyErr) {
        console.error('Failed to load privacy settings:', privacyErr);
        setUser(updatedUser);
        localStorage.setItem("chatapp_user", JSON.stringify(updatedUser));
      }

      // Navigate to home
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Profile setup error:', error);
      setErrorMessage(
        error.response?.data?.error || 'Failed to complete profile setup. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!userData) {
    return (
      <div className="auth-page">
        <div className="loading-message">Loading...</div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card-grid auth-card-grid--compact">
        <div className="auth-card-info">
          <div className="auth-logo-chip">ChatApp</div>
          <h1>Complete Your Profile</h1>
          <p>
            Welcome! Please set up your profile to start chatting with your friends and colleagues.
          </p>
        </div>

        <div className="auth-container auth-container--panel">
          <h2>Profile Setup</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="profile-setup-email">
              <label>Email</label>
              <div className="email-display">{userData.email}</div>
            </div>

            <input
              name="username"
              placeholder="Username *"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              autoFocus
            />

            <textarea
              name="bio"
              placeholder="Bio (Optional)"
              value={bio}
              onChange={e => setBio(e.target.value)}
              maxLength="150"
              rows="3"
            />

            {/* Profile Image Upload */}
            <div className="image-upload-container">
              <label htmlFor="profile-image" className="image-upload-label">
                {imagePreview ? 'Change Profile Image' : 'Add Profile Image (Optional)'}
              </label>
              <input
                id="profile-image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              {imagePreview && (
                <div className="image-preview">
                  <button
                    type="button"
                    aria-label="Remove selected image"
                    className="image-remove-pill"
                    onClick={() => {
                      setProfileImage(null);
                      setImagePreview(null);
                    }}
                  >
                    <span>&times;</span>
                  </button>
                  <img src={imagePreview} alt="Profile preview" />
                </div>
              )}
            </div>

            <button type="submit" disabled={loading}>
              {loading ? "Setting up..." : "Continue to Chat"}
            </button>
          </form>

          {errorMessage && (
            <div className="auth-error-banner">
              {errorMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileSetup;
